import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import { useEquityMarket } from '../contexts/EquityMarketContext';

type CtaCardProps = {
  icon: string;
  iconVariant: 'success' | 'primary' | 'secondary';
  title: string;
  description: string;
  subDescription: string;
  buttonLabel: string;
  buttonVariant: ButtonVariants;
  onAction: () => void;
  actionDisabled?: boolean;
};

const CtaCard: FC<CtaCardProps> = ({
  icon,
  iconVariant,
  title,
  description,
  subDescription,
  buttonLabel,
  buttonVariant,
  onAction,
  actionDisabled = false,
}) => {
  const iconBgClass =
    iconVariant === 'secondary' ? 'bg-light' : `bg-${iconVariant}-dim`;
  const iconTextClass = `text-${iconVariant}`;

  return (
    <div
      className={`card card-bordered h-100 border-top-${iconVariant}`}
      style={{ borderTopWidth: `3px` }}
    >
      <div className="card-inner h-100 d-flex flex-column justify-content-between">
        <div className="d-flex align-items-start mb-3">
          <div
            className={`icon-circle-xl ${iconBgClass} ${iconTextClass} mr-3 flex-shrink-0`}
          >
            <em className={`icon ni ${icon}`} />
          </div>
          <div>
            <h6 className="mb-1">{title}</h6>
            <p className="text-soft mb-0">{description}</p>
            <p className="text-soft mb-2">{subDescription}</p>
          </div>
        </div>

        <Button
          text={buttonLabel}
          variant={buttonVariant as ButtonVariants}
          onClick={onAction}
          disabled={actionDisabled}
        />
      </div>
    </div>
  );
};

const EquityMarketCtaCards: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useEquityMarket();

  return (
    <div className="nk-block nk-block-spacing">
      <h5 className="nk-block-title mb-3">{t('WhatDoYouWantToDo')}</h5>
      <div className="row gy-gs">
        {/* Sell Shares CTA */}
        <div className="col-12 col-md-6 col-lg-4">
          <CtaCard
            icon="ni-arrow-up"
            iconVariant="success"
            title={t('SellMyShares')}
            description={t('PublishSellOffer')}
            subDescription={t('YouHaveSharesAvailable', {
              count: data.partnerShares.total,
            })}
            buttonLabel={t('CreateSellOffer')}
            buttonVariant={ButtonVariants.SUCCESS}
            onAction={actions.handleCreateSellOffer}
          />
        </div>

        {/* Buy Shares CTA */}
        <div className="col-12 col-md-6 col-lg-4">
          <CtaCard
            icon="ni-arrow-down"
            iconVariant="secondary"
            title={t('BuyShares')}
            description={t('ViewAvailableSellOffers')}
            subDescription={t('ActiveSellOffersCount', {
              count: data.sellOffersSummary.offersCount,
            })}
            buttonLabel={t('ViewSellOffers')}
            buttonVariant={ButtonVariants.LIGHT}
            onAction={actions.handleViewSellOffers}
          />
        </div>

        {/* Launch Buy Offer CTA */}
        <div className="col-12 col-md-6 col-lg-4">
          <CtaCard
            icon="ni-offer"
            iconVariant="primary"
            title={t('LaunchBuyOffer')}
            description={t('PublishThatYouWantToBuy')}
            subDescription={t('IfYouDontFindWhatYouAreLookingFor')}
            buttonLabel={t('CreateBuyOffer')}
            buttonVariant={ButtonVariants.PRIMARY}
            onAction={actions.handleCreateBuyOffer}
          />
        </div>
      </div>
    </div>
  );
};

export default EquityMarketCtaCards;
